import React from 'react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { ArrowRight, ChevronDown, ShieldCheck, Lock } from 'lucide-react';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from '../ui/dropdown-menu';
import { LandingTierCards } from './LandingTierCards';
import { isBetaActive } from '../../utils/betaMode';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const LandingHeader = ({ isLoggedIn, isChecking, handleGetStarted, setShowAccessRequestModal }) => (
  <header className="container mx-auto px-3 sm:px-6 py-4 sm:py-8">
    <nav className="flex flex-col sm:flex-row justify-between items-center gap-3 sm:gap-0">
      <div className="flex items-center space-x-1 sm:space-x-2 cursor-pointer" onClick={() => window.location.href = '/'}>
        <img src="https://customer-assets.emergentagent.com/job_3876ffd2-0b67-4d0f-be27-32eb0667fc3a/artifacts/g551041i_MIF%20Logo%20White.png" alt="MyInvestFolio Logo" className="w-8 h-8 sm:w-10 sm:h-10 object-contain" />
        <h1 className="text-lg sm:text-2xl lg:text-3xl font-serif font-bold text-slate-900">My<span className="text-green-600">Invest</span><span className="text-blue-600">Folio</span></h1>
      </div>
      <div className="flex flex-col items-center sm:items-end space-y-2">
        <div className="flex items-center flex-wrap justify-center sm:justify-end gap-1 sm:gap-2">
          <Button variant="ghost" onClick={() => window.location.href = '/about-us'} className="text-slate-600 hover:text-emerald-600 hover:bg-emerald-50 transition-colors text-xs sm:text-sm px-2 sm:px-3">About Us</Button>
          <Button variant="ghost" onClick={() => window.location.href = '/our-philosophy'} className="text-slate-600 hover:text-emerald-600 hover:bg-emerald-50 transition-colors text-xs sm:text-sm px-2 sm:px-3 hidden md:inline-flex">Philosophy</Button>
          <Button variant="ghost" onClick={() => window.location.href = '/perspectives'} className="text-slate-600 hover:text-emerald-600 hover:bg-emerald-50 transition-colors text-xs sm:text-sm px-2 sm:px-3">Perspectives</Button>
          <Button variant="ghost" onClick={() => document.getElementById('features')?.scrollIntoView({ behavior: 'smooth' })} className="text-slate-600 hover:text-emerald-600 hover:bg-emerald-50 transition-colors text-xs sm:text-sm px-2 sm:px-3 hidden lg:inline-flex">Features</Button>
          <Button variant="ghost" onClick={() => document.getElementById('investor-questions')?.scrollIntoView({ behavior: 'smooth' })} className="text-slate-600 hover:text-emerald-600 hover:bg-emerald-50 transition-colors text-xs sm:text-sm px-2 sm:px-3"><span className="hidden md:inline">Investor </span>Questions</Button>
          <Button variant="ghost" onClick={() => document.getElementById('advisor-resources')?.scrollIntoView({ behavior: 'smooth' })} className="text-slate-600 hover:text-emerald-600 hover:bg-emerald-50 transition-colors text-xs sm:text-sm px-2 sm:px-3">Trusted Professionals</Button>
          {!isChecking && (isLoggedIn ? (
            <Button data-testid="header-dashboard-btn" onClick={() => window.location.href = '/dashboard'} className="bg-emerald-600 hover:bg-emerald-700 text-white font-semibold text-xs sm:text-sm px-3 sm:px-4 whitespace-nowrap"><span className="hidden sm:inline">Go to </span>Dashboard</Button>
          ) : (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button data-testid="header-get-started-btn" className="bg-emerald-600 hover:bg-emerald-700 text-white font-semibold text-xs sm:text-sm px-3 sm:px-4 whitespace-nowrap">
                  Get Started <ChevronDown className="ml-1 w-3.5 h-3.5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-64 z-[60]">
                {isBetaActive() ? (
                  <div
                    data-testid="header-get-started-after-beta-note"
                    className="px-3 py-3 text-center"
                  >
                    <div className="flex items-center justify-center gap-2 text-slate-500 font-semibold text-sm">
                      <Lock className="w-3.5 h-3.5" />
                      Available after Beta
                    </div>
                  </div>
                ) : (
                  <>
                    <DropdownMenuItem
                      data-testid="header-get-started-continuity"
                      onClick={handleGetStarted}
                      className="cursor-pointer font-semibold text-blue-700 focus:text-blue-800 focus:bg-blue-50"
                    >
                      Continuity System
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      data-testid="header-get-started-portfolio"
                      onClick={() => setShowAccessRequestModal(true)}
                      className="cursor-pointer font-semibold text-emerald-700 focus:text-emerald-800 focus:bg-emerald-50"
                    >
                      Portfolio Clarity
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          ))}
        </div>
        {!isChecking && !isLoggedIn && (
          <button onClick={() => window.location.href = '/login'} className="text-[10px] sm:text-xs text-slate-500 hover:text-emerald-600 transition-colors underline-offset-2 hover:underline">Already have an account? <span className="font-medium">Sign in</span></button>
        )}
      </div>
    </nav>
  </header>
);

export const HeroSection = ({ isLoggedIn, isChecking, handleGetStarted, handleRequestInvitation, pricing }) => (
  <section className="container mx-auto px-3 sm:px-6 py-8 sm:py-16">
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 sm:gap-12 items-center">
      <div className="lg:col-span-7 space-y-6 sm:space-y-8">
        <div className="space-y-4 sm:space-y-6">
          <Badge className="bg-blue-100 text-blue-800 border-blue-200 text-xs sm:text-sm" data-testid="hero-invitation-only-badge">Two ways in</Badge>
          <h1 className="font-serif font-bold leading-tight">
            <span className="block text-slate-900 text-base sm:text-xl md:text-2xl lg:text-2xl xl:text-3xl 2xl:text-4xl lg:whitespace-nowrap" data-testid="hero-line-promise">
              Clarity Today. Continuity Over Generations.
            </span>
            <span className="block mt-2 sm:mt-3 text-emerald-600 text-sm sm:text-base md:text-lg lg:text-lg xl:text-xl 2xl:text-2xl lg:whitespace-nowrap" data-testid="hero-line-category">
              Your Personal System for Wealth Record
            </span>
            <span className="block mt-1.5 sm:mt-2 text-blue-600 text-xs sm:text-sm md:text-base lg:text-base xl:text-lg 2xl:text-xl font-semibold lg:whitespace-nowrap" data-testid="hero-line-audience">
              For Discerning, Independent Investors
            </span>
          </h1>
          <p className="text-xs sm:text-sm md:text-base lg:text-base xl:text-lg 2xl:text-xl font-medium body-text max-w-2xl">Two ways in. <strong>Continuity System</strong> — your private record of what you own, ready when it matters. <strong>Portfolio Clarity</strong> — the full picture, including portfolio allocation, performance and tax data.</p>
        </div>
        {isLoggedIn ? (
          <div className="flex flex-col sm:flex-row gap-4">
            <Button data-testid="hero-dashboard-btn" onClick={handleGetStarted} size="lg" className="bg-emerald-600 hover:bg-emerald-700 text-white shadow-lg hover:shadow-xl transition-all duration-300 rounded-full text-base sm:text-lg px-8 sm:px-12 py-3 sm:py-4 font-medium">Go to Dashboard<ArrowRight className="ml-2 w-4 h-4 sm:w-5 sm:h-5" /></Button>
            <Button variant="outline" size="lg" className="portfolio-button-secondary text-base sm:text-lg px-6 sm:px-8 py-3 sm:py-4" onClick={() => document.getElementById('features')?.scrollIntoView({ behavior: 'smooth' })}>Learn More</Button>
          </div>
        ) : null}
        <LandingTierCards
          handleGetStarted={handleGetStarted}
          handleRequestInvitation={handleRequestInvitation || handleGetStarted}
          pricing={pricing}
        />
        {!isLoggedIn && !isChecking && (
          <button onClick={() => window.location.href = '/login'} className="text-xs sm:text-sm text-slate-500 hover:text-emerald-600 transition-colors text-left sm:text-center block mt-2">Already have an account? <span className="font-semibold underline underline-offset-2">Sign in</span></button>
        )}
        <div className="flex flex-col gap-2 max-w-full" data-testid="hero-privacy-anchor">
          <div className="flex items-start gap-2 text-sm sm:text-base text-emerald-900/90">
            <ShieldCheck className="w-4 h-4 sm:w-5 sm:h-5 text-emerald-700 shrink-0 mt-0.5" />
            <ul className="flex flex-col sm:flex-row sm:flex-wrap gap-y-1 sm:gap-x-0 font-medium leading-snug list-none m-0 p-0">
              <li>Always encrypted</li>
              <li aria-hidden className="hidden sm:inline-block mx-2 text-emerald-700/50">·</li>
              <li>No password stored</li>
              <li aria-hidden className="hidden sm:inline-block mx-2 text-emerald-700/50">·</li>
              <li>No outside AI tools</li>
            </ul>
          </div>
          <button
            type="button"
            onClick={() => document.getElementById('how-encrypted')?.scrollIntoView({ behavior: 'smooth' })}
            className="ml-6 sm:ml-7 self-start text-sm sm:text-base font-bold text-emerald-800 hover:text-emerald-900 underline underline-offset-4 decoration-2 decoration-emerald-600 hover:decoration-emerald-900 transition-colors"
            data-testid="hero-privacy-link"
            aria-label="Read how your data is protected"
          >
            How we protect your data →
          </button>
        </div>
        <div className="flex items-center space-x-8 pt-4">
          <div className="text-center"><div className="text-2xl font-mono font-bold text-slate-900">14</div><div className="text-sm text-slate-500">Investment Types</div></div>
          <div className="text-center"><div className="text-2xl font-mono font-bold text-slate-900">150+</div><div className="text-sm text-slate-500">Currencies</div></div>
          <div className="text-center"><div className="text-2xl font-mono font-bold text-slate-900">100%</div><div className="text-sm text-slate-500">Secure</div></div>
        </div>
        <p className="text-base sm:text-lg text-slate-700 font-medium italic" data-testid="hero-trust-anchor">Trusted by discerning investors</p>
      </div>
      <div className="lg:col-span-5">
        <div className="relative">
          <img
            src="https://images.unsplash.com/photo-1692037198805-c7421032c5cf?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDF8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMGZpbmFuY2lhbCUyMGdyb3d0aCUyMDNkJTIwbWluaW1hbGlzdCUyMGJsdWUlMjBncmVlbnxlbnwwfHx8fDE3NjgwNTEzNjJ8MA&ixlib=rb-4.1.0&q=85"
            alt="Portfolio Analytics"
            className="w-full rounded-2xl shadow-[0_10px_30px_-5px_rgba(0,0,0,0.1)]"
            style={{ filter: 'hue-rotate(-25deg) saturate(0.82)' }}
            data-testid="hero-image-tinted"
          />
          {/* Emerald harmonisation applied via hue-rotate + saturate on the image itself
              (Feb 2026): shifts cobalt toward teal/emerald and mellows the intensity so
              the hero reads as part of the site's brand palette. Composition untouched. */}
          <div className="absolute inset-0 bg-gradient-to-t from-slate-900/20 to-transparent rounded-2xl pointer-events-none"></div>
        </div>
      </div>
    </div>
  </section>
);
